// apps/admin/src/features/legal-entities/TimezonePicker.tsx
//
// Phase 8.2 Wave 4 Task 23 — Searchable IANA timezone picker.
//
//  (Legal Entity Timezone Field UI Amendment #1): the existing
// `<Select>` listing all ~400 IANA timezones is unusable. Replaces that
// with a typeahead-style searchable input + filtered dropdown so that
// admin can find "Asia/Hong_Kong" by typing "hong" or "hk".
//
// Source: `Intl.supportedValuesOf('timeZone')` (Chrome 99+ / Safari 15.4+ /
// Firefox 100+). Curated fallback for older runtimes so the picker still
// works when run inside legacy admin browser sessions.
//
// Default: `Asia/Hong_Kong` (Printway HK default entity).
//
// Test ID per PLAN/task-23.md: `legal-entity-timezone-input`.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, cn } from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Loader — Intl.supportedValuesOf when available, curated fallback otherwise.
// Memoized at module level so the ~400-entry list isn't rebuilt per render.
// ---------------------------------------------------------------------------

function loadAllTimezones(): string[] {
  type IntlWithSupported = typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  const intl = Intl as IntlWithSupported;
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      const list = intl.supportedValuesOf('timeZone');
      if (Array.isArray(list) && list.length > 0) return list;
    } catch {
      // Fallthrough to curated list below.
    }
  }
  return [
    'UTC',
    'Asia/Hong_Kong',
    'Asia/Ho_Chi_Minh',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Seoul',
    'Asia/Bangkok',
    'Asia/Jakarta',
    'Asia/Kuala_Lumpur',
    'Asia/Manila',
    'Asia/Taipei',
    'Asia/Kolkata',
    'Asia/Dubai',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Sao_Paulo',
    'America/Mexico_City',
    'Europe/London',
    'Europe/Berlin',
    'Europe/Paris',
    'Europe/Madrid',
    'Europe/Rome',
    'Europe/Amsterdam',
    'Europe/Zurich',
    'Europe/Moscow',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Pacific/Auckland',
    'Africa/Cairo',
    'Africa/Johannesburg',
  ];
}

const ALL_TIMEZONES = loadAllTimezones();

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TimezonePickerProps {
  value: string;
  onChange: (next: string) => void;
  /** Show under input — flagged warning for legacy/unset entries. */
  warning?: string | null;
  /** Validation error from form-level schema. */
  error?: string | null;
  /** Disable input (e.g. while parent form submitting). */
  disabled?: boolean;
  /** Test id override — defaults to `legal-entity-timezone-input`. */
  testId?: string;
  id?: string;
}

// ---------------------------------------------------------------------------
// Component — typeahead input with filtered dropdown.
// ---------------------------------------------------------------------------

export function TimezonePicker({
  value,
  onChange,
  warning,
  error,
  disabled,
  testId = 'legal-entity-timezone-input',
  id = 'legal-entity-timezone',
}: TimezonePickerProps): JSX.Element {
  const { t } = useTranslation('finance');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep query in sync with parent-driven value (edit mode hydration).
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Click-outside dismiss.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        // Snap back to last committed value if user typed garbage.
        setQuery(value);
      }
    }
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open, value]);

  const filtered = useMemo<string[]>(() => {
    // UAT #1081: on focus the input is pre-filled with the committed value
    // (e.g. "Asia/Hong_Kong"), so filtering by it would leave only that single
    // entry. Treat "query still equals the committed value" as empty so the
    // full list is shown; typed-search kicks in as soon as query diverges.
    const q = query === value ? '' : query.trim().toLowerCase();
    if (q === '') return ALL_TIMEZONES.slice(0, 100);
    // Match continent OR city OR substring (e.g. "hong" → Asia/Hong_Kong).
    const out: string[] = [];
    for (const tz of ALL_TIMEZONES) {
      if (tz.toLowerCase().includes(q)) {
        out.push(tz);
        if (out.length >= 100) break;
      }
    }
    return out;
  }, [query, value]);

  function commit(tz: string): void {
    onChange(tz);
    setQuery(tz);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t('legal_entity.timezone.searchPlaceholder')}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        aria-invalid={error ? 'true' : 'false'}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        role="combobox"
        data-testid={testId}
      />
      {open && filtered.length > 0 ? (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-pw-border bg-pw-bg shadow-lg"
          data-testid="legal-entity-timezone-listbox"
        >
          {filtered.map((tz) => {
            const active = tz === value;
            return (
              <li
                key={tz}
                role="option"
                aria-selected={active}
                className={cn(
                  'cursor-pointer px-3 py-1.5 text-sm hover:bg-pw-surface',
                  active && 'bg-pw-primary/10 font-medium text-pw-primary',
                )}
                onMouseDown={(e) => {
                  // mousedown (not click) so it fires before input blur.
                  e.preventDefault();
                  commit(tz);
                }}
              >
                {tz}
              </li>
            );
          })}
        </ul>
      ) : null}
      {open && filtered.length === 0 ? (
        <p
          className="absolute z-50 mt-1 w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-xs text-pw-text-muted shadow-lg"
          role="status"
        >
          {t('legal_entity.timezone.noMatch', { query })}
        </p>
      ) : null}
      {error ? (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
          {error}
        </p>
      ) : warning ? (
        <p
          className="mt-1 text-xs text-amber-600 dark:text-amber-300"
          role="status"
          data-testid="legal-entity-timezone-warning"
        >
          {warning}
        </p>
      ) : (
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('legal_entity.timezone.helper')}
        </p>
      )}
    </div>
  );
}
